import { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { EmailService } from "@/services/email.service";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/auth/Auth";
import { useCookies } from "react-cookie";

export const useEmailConfirmation = () => {
  const { t } = useTranslation("common");
  const [timeLeft, setTimeLeft] = useState(60);
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { fetchUserData, saveToken } = useAuth();
  const [cookies] = useCookies(["mundpayToken"]);

  useEffect(() => {
    const loginTimestamp = localStorage.getItem('@loginTimestamp');
    if (loginTimestamp) {
      setIsButtonDisabled(false);
      localStorage.removeItem('@loginTimestamp');
    }
  }, []);

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${String(minutes).padStart(1, "0")}:${String(
      remainingSeconds
    ).padStart(2, "0")}`;
  };

  const handleResendEmail = useCallback(async () => {
    setIsLoading(true);
    try {
      await EmailService.resendEmail();
      setIsButtonDisabled(true);
      setTimeLeft(60);
      toast({ variant: "success", description: t("email_warning.resend_success") });
    } catch (error) {
      toast({ variant: "error", description: t("email_warning.resend_error") });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  const verifyEmail = useCallback(async () => {
    setIsLoading(true)
    const params = new URLSearchParams(location.search);
    const encodedUrl = params.get("url");

    if (encodedUrl) {
      const decodedUrl = decodeURIComponent(encodedUrl);
      try {
        await EmailService.getVerification(decodedUrl);
        const tokenFromCookie = cookies["mundpayToken"];
        if (tokenFromCookie) {
          saveToken(tokenFromCookie);
          await fetchUserData();
          const redirectTo = localStorage.getItem('redirectAfterEmailVerification') || '/email-verified';
          navigate(redirectTo);
        } else {
          navigate("/login");
        }
      } catch (error) {
        toast({
          variant: "error",
          description: t("emailVerifyFail"),
        });
      } finally {
        setIsLoading(false)
      }
    }
  }, [location.search, navigate, toast, fetchUserData]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("url")) {
      verifyEmail();
    }
  }, [location.search, verifyEmail]);

  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;
    if (isButtonDisabled) {
      timer = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev === 1) {
            setIsButtonDisabled(false);
            clearInterval(timer as NodeJS.Timeout);
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [isButtonDisabled]);

  return {
    timeLeft: formatTime(timeLeft),
    isButtonDisabled: isButtonDisabled || isLoading,
    isLoading,
    handleResendEmail,
  };
};